import { useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import { Route, Routes as ReactRouterRoutes, Navigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import { Loader } from '@hivebrite/design-system/Loader';

import {
  SessionInfoProvider,
  useSessionInfoContext,
} from 'hb-react/frontoffice/contexts/SessionInfoContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { MEMBERSHIP_PURCHASE_PROCESS_TYPES } from 'hb-react/shared/apps/Memberships/constants';
import CreateCart from 'hb-react/shared/apps/Memberships/containers/CreateCart';
import MembershipsIndex from 'hb-react/shared/apps/Memberships/containers/MembershipsIndex';
import OrderCompleted from 'hb-react/shared/apps/Memberships/containers/OrderCompleted';
import PaymentDraftCart from 'hb-react/shared/apps/Memberships/containers/Payment';
import PurchaseMembership from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership';
import {
  PAGES as MEMBERSHIP_PAGES,
  STEPS as MEMBERSHIP_STEPS,
} from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership/constants';
import { SingleMembership } from 'hb-react/shared/apps/Memberships/containers/SingleMembership';
import ErrorBoundary from 'hb-react/shared/components/ErrorBoundary';
import { ShoppingCardProvider } from 'hb-react/shared/components/ShoppingCard/ShoppingCardContext';
import { usePageMenuName, PAGES } from 'hb-react/shared/hooks/usePageMenuName';
import {
  hideFooterFrontLinks,
  showFooterFrontLinks,
} from 'hb-react/shared/utils/footerFrontLinks';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

// TODO: Remove on cart factory ∧
const MembershipsV2AppRoutes = ({ config }) => {
  const { memberships: membershipsSessionInfo } = useSessionInfoContext();
  const { currentNetwork } = useDefaultAppContexts();

  const configMenuName = config?.custom?.page?.menu_name;
  const pageMenuName = usePageMenuName(
    PAGES.MEMBERSHIPS,
    s__('Memberships|Memberships'),
    { enabled: !configMenuName },
  );
  const menuName = configMenuName || pageMenuName;

  useEffect(() => {
    hideFooterFrontLinks();

    return () => showFooterFrontLinks();
  }, []);

  if (!membershipsSessionInfo) {
    return <Loader fillParent />;
  }

  const purchaseStrategyType = membershipsSessionInfo?.programme
    ?.enable_multiple_purchase
    ? MEMBERSHIP_PURCHASE_PROCESS_TYPES.MULTIPLE
    : MEMBERSHIP_PURCHASE_PROCESS_TYPES.SINGLE;

  const renderDocumentTitle = (step = '') => (
    <DocumentTitle
      title={generatePageTitle({
        title: `${menuName} ${step}`,
        networkName: getPageTitleFromNetwork(currentNetwork),
      })}
    />
  );

  const defaultPage =
    MEMBERSHIP_PAGES[MEMBERSHIP_STEPS.REGISTRATION].toLowerCase();

  return (
    <ReactRouterRoutes>
      <Route path="order/create" element={<CreateCart />} />

      <Route path="order/:cartId/*">
        <Route index element={<Navigate to={defaultPage} replace />} />
        <Route
          path=":page"
          element={
            <Container>
              <ShoppingCardProvider>
                <PurchaseMembership
                  displayDocumentTitle={renderDocumentTitle}
                />
              </ShoppingCardProvider>
            </Container>
          }
        />
        <Route
          path="completed"
          element={
            <Container>
              {renderDocumentTitle(__('Payment completed'))}
              <OrderCompleted />
            </Container>
          }
        />
        <Route
          path="payment"
          element={
            <Container>
              {renderDocumentTitle(__('Payment'))}
              <PaymentDraftCart />
            </Container>
          }
        />
      </Route>

      <Route
        index
        element={
          <>
            {renderDocumentTitle()}
            <MembershipsIndex
              appPageConfig={config?.custom?.page}
              readOnly={config?.custom?.readOnly}
              purchaseStrategyType={purchaseStrategyType}
            />
          </>
        }
      />

      <Route
        path=":id"
        element={
          <Container>
            <SingleMembership
              membershipId={window.location.href.split('/').pop()}
              purchaseStrategyType={MEMBERSHIP_PURCHASE_PROCESS_TYPES.SINGLE}
              readOnly={config?.custom?.readOnly}
            />
          </Container>
        }
      />
    </ReactRouterRoutes>
  );
};

const MembershipsV2App = ({ config }) => (
  <SessionInfoProvider type="memberships">
    <ErrorBoundary
      context={{
        tags: {
          module: 'memberships_v2',
        },
      }}
    >
      <Box id="memberships-v2-app-container" display="block" width="100%">
        <MembershipsV2AppRoutes config={config} />
      </Box>
    </ErrorBoundary>
  </SessionInfoProvider>
);

export default MembershipsV2App;
